import { findAnimationById } from 'Pods/ThreeDStudio/Services/Constants/animations'
import { getMultiViewConstraints } from 'Pods/ThreeDStudio/Shared/Utils/getMultiViewConstraints'
import { MATERIAL, PROVIDER } from 'Pods/ThreeDStudio/Store/constants'

const hasInputImage = settings => settings.inputImage != null && settings.inputImage !== ''

// The single-image inputImage and the multi-view inputViewImages are separate inputs:
// whichever uploader is active decides what is sent, and the other one is ignored. No
// angle is mandatory in multi-view. Views the active provider does not accept are left
// out, and the rest follow the provider's own order, since Meshy and Tripo3D read the
// list positionally. Capped at maxItems as a backstop for the uploader's own limit.
const multiViewEntries = (settings, properties) => {
  const { viewTypes, maxItems } = getMultiViewConstraints(properties?.input_image)

  return viewTypes
    .filter(viewType => settings.inputViewImages?.[viewType]?.id != null)
    .slice(0, maxItems)
    .map(viewType => ({ asset_id: settings.inputViewImages[viewType].id, view_type: viewType }))
}

const inputImageParam = (settings, properties) => {
  if (settings.isMultiView) {
    const entries = multiViewEntries(settings, properties)

    return entries.length > 0 ? entries : undefined
  }

  return hasInputImage(settings) ? [{ asset_id: settings.inputImage }] : undefined
}

/**
 * Returns the value for a schema param from store state.
 * input_image is built as [{ asset_id: inputImage }] when inputImage is set; in multi-view
 * mode it becomes [{ asset_id, view_type }, ...] from inputViewImages and multiview_input is true.
 * animation resolves the picked animation slug to the active engine's id.
 * Reads from state.modelSettings (generation params namespace).
 *
 * @param {string} schemaKey - Canonical param name
 * @param {string} settingsKey - Store slice holding the form values
 * @param {Object} state - ThreeDStudio store state
 * @param {Object} [properties] - The schema's input_schema.properties, used for the
 *   per-provider multi-view limits on input_image
 */
export function getStoreValueForParam(schemaKey, settingsKey, state, properties = undefined) {
  const settings = state[settingsKey]
  const mapping = {
    prompt: () => settings.prompt,
    face_count: () => settings.targetPolycount,
    target_polycount: () => settings.targetPolycount,
    polygon_type: () => settings.polygonType,
    smart_low_poly: () => settings.isSmartLowPoly,
    ultra_mode: () => settings.extraQuality,
    input_image: () => inputImageParam(settings, properties),
    multiview_input: () => (
      settings.isMultiView && multiViewEntries(settings, properties).length > 0 ? true : undefined
    ),
    material: () => settings.material,
    // The Render tab stores the whole style card ({ id, name, image }); only the id
    // identifies it to the backend.
    style: () => settings.style?.id,
    texture_quality: () => settings.textureQuality,
    enable_pbr: () => settings.material === MATERIAL.PBR,
    geometry_only: () => settings.material === MATERIAL.GEOMETRY_ONLY,
    face_level: () => settings.faceLevel,
    input_3d: () => state.viewer.inputThreeD != null && state.viewer.inputThreeD !== ''
      ? [{ asset_id: state.viewer.inputThreeD }]
      : undefined,
    // The Animate tab stores a local library slug; the engines identify the clip
    // differently, so the same schema key resolves to Tripo3D's preset name or
    // Meshy's numeric library id (renamed to action_id by keyToConvert).
    animation: () => {
      const entry = findAnimationById(settings.animationId)

      return state.schema.enhancedProvider === PROVIDER.TRIPO3D
        ? entry?.tripoPreset
        : entry?.actionId
    },
  }
  const mappedFunction = mapping[schemaKey]
  return mappedFunction ? mappedFunction() : undefined
}
